<script setup lang="ts">
import { getBgColorByString } from '@/js/utils/common';
import Icon from '@ui/components/icon.vue';
import MenuBadge, { type TopBarMenuBadgeInterface } from '@app/components/parts/topBar/menuBadge.vue';
import { flashMessage } from '@utils/flashMessage';
import { FlashMessageThemeEnum } from '@/js/enums/FlashMessageThemeEnum';
import { translate } from '@utils/I18n';

declare const __RECRUITIS_CROSS_COMPANY_CSRF_TOKEN: string;
declare const __RECRUITIS_LOGOUT_CSRF_TOKEN: string;

type MenuLinkMethod = 'GET' | 'POST';
type MenuLinkCount = TopBarMenuBadgeInterface['count'];

interface MenuLink {
    text: string;
    url: string;
    type?: never;
    method?: MenuLinkMethod;
    action?: 'logout';
    icon?: string;
    badge?: TopBarMenuBadgeInterface | null;
    count?: MenuLinkCount;
    color?: string;
}

interface MenuDivider {
    type: 'divider';
}

type MenuItem = MenuLink | MenuDivider;

interface MenuColumn {
    header?: string;
    items: MenuItem[];
}

interface Menu {
    columns: MenuColumn[];
}

interface MenuLinkResponse {
    redirect?: string;
}

const props = withDefaults(
    defineProps<{
        menu: Menu;
        inDropDown: boolean;
        isOpen: boolean;
    }>(),
    {
        menu: () => {
            return { columns: [] };
        },
        inDropDown: true,
        isOpen: true,
    },
);

function hasCount(count: MenuLinkCount) {
    return typeof count !== 'undefined';
}

function hasBadge(menuLink: MenuLink) {
    return hasCount(menuLink.count) || Boolean(menuLink.badge);
}

const logoutCsrfToken = typeof __RECRUITIS_LOGOUT_CSRF_TOKEN === 'undefined' ? '' : __RECRUITIS_LOGOUT_CSRF_TOKEN;

function handleLogoutSubmit(event: SubmitEvent): void {
    if (logoutCsrfToken.length === 0) {
        event.preventDefault();
        console.error('Logout request aborted: native CSRF token is unavailable.');
        flashMessage(FlashMessageThemeEnum.Error, translate('request.csrf.validation.failed'));
    }
}

async function handleMenuLinkClick(event: MouseEvent, menuLink: MenuLink): Promise<void> {
    if (menuLink.method !== 'POST' || menuLink.action === 'logout') {
        return;
    }

    event.preventDefault();

    if (
        typeof __RECRUITIS_CROSS_COMPANY_CSRF_TOKEN === 'undefined' ||
        __RECRUITIS_CROSS_COMPANY_CSRF_TOKEN.length === 0
    ) {
        console.error('Account switch request aborted: native CSRF token is unavailable.');
        return;
    }

    try {
        const response = await fetch(menuLink.url, {
            method: 'POST',
            headers: { 'X-CSRF-Token': __RECRUITIS_CROSS_COMPANY_CSRF_TOKEN },
        });

        if (!response.ok) {
            console.error(`Account switch failed with status ${response.status}.`);
            return;
        }

        const data: MenuLinkResponse = await response.json();
        if (data.redirect) {
            window.location.href = data.redirect;
        }
    } catch (error) {
        console.error('Account switch request failed.', error);
    }
}
</script>
<template>
    <div
        class="columns-1 p-2 text-sm text-white sm:pt-0 lg:origin-top-left lg:px-3 lg:pb-3 lg:text-[13px] xl:text-base"
        :class="
            [
                props.menu.columns.length > 1 && 'w-full sm:w-128 sm:columns-2',
                props.menu.columns.length > 2 && 'w-full lg:w-[1000px] lg:columns-3 xl:w-[1050px]',
            ].join(' ')
        "
    >
        <div
            v-for="(column, index) in props.menu.columns"
            :key="index"
            class="inline-flex w-full flex-col align-top"
            :class="column.header && 'lg:-mt-1'"
        >
            <h2
                v-if="column.header"
                class="font-brand -mb-1 mt-2 flex items-center px-3 text-base font-semibold leading-tight text-white sm:mt-4 lg:mt-6 xl:text-lg"
            >
                {{ column.header }}
            </h2>
            <ul
                class="m-0 list-none p-0 text-left"
                :class="column.header ? 'mt-3' : 'sm:pt-2 lg:pt-3'"
            >
                <li
                    v-for="(menuLink, idx) in column.items"
                    :key="idx"
                >
                    <form
                        v-if="menuLink.type !== 'divider' && menuLink.method === 'POST' && menuLink.action === 'logout'"
                        :action="menuLink.url"
                        method="post"
                        class="m-0"
                        @submit="handleLogoutSubmit"
                    >
                        <input
                            type="hidden"
                            name="token"
                            :value="logoutCsrfToken"
                        >
                        <button
                            type="submit"
                            class="group/link flex w-full cursor-pointer items-center gap-2 rounded border-0 bg-transparent px-3 py-1.5 text-left font-sans text-gray-400 [font-size:inherit] hover:bg-gray-900 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white lg:px-4 lg:py-2.5"
                        >
                            <Icon
                                v-if="menuLink.icon"
                                :icon="menuLink.icon"
                                size="xsmall"
                                class="group-hover/link:text-brand-400 -ml-1 flex-shrink-0 text-gray-400 lg:-ml-2"
                            ></Icon>
                            <span class="flex-grow">{{ menuLink.text }}</span>
                            <MenuBadge
                                v-if="hasBadge(menuLink)"
                                :badge="menuLink.badge"
                                :count="menuLink.count"
                                :should-load="props.isOpen"
                                :class="getBgColorByString(menuLink)"
                            ></MenuBadge>
                        </button>
                    </form>
                    <a
                        v-else-if="menuLink.type !== 'divider'"
                        class="group/link flex items-center gap-2 rounded px-3 py-1.5 font-sans text-gray-400 no-underline hover:bg-gray-900 hover:text-white lg:px-4 lg:py-2.5"
                        :href="menuLink.url"
                        @click="handleMenuLinkClick($event, menuLink)"
                    >
                        <Icon
                            v-if="menuLink.icon"
                            :icon="menuLink.icon"
                            size="xsmall"
                            class="group-hover/link:text-brand-400 -ml-1 flex-shrink-0 text-gray-400 lg:-ml-2"
                        ></Icon>
                        <span class="flex-grow">{{ menuLink.text }}</span>
                        <MenuBadge
                            v-if="hasBadge(menuLink)"
                            :badge="menuLink.badge"
                            :count="menuLink.count"
                            :should-load="props.isOpen"
                            :class="getBgColorByString(menuLink)"
                        ></MenuBadge>
                    </a>
                    <div
                        v-else
                        class="-mx-2 my-2 flex h-px bg-gray-600 lg:-mx-3 lg:my-3"
                    ></div>
                </li>
            </ul>
        </div>
    </div>
</template>
